import type { GanttConfigPanelValues } from "./gantt-config-panel";
import { AssignmentBarColors } from "./prop-types";

type Translate = (key: string) => string;

type ToggleHandler = (value: boolean) => void;
type NumberHandler = (value: number) => void;
type StringHandler = (value: string) => void;

export type ConfigurePanelItem = {
   id?: string;
   [key: string]: unknown;
};

export type ConfigurePanelStrip = {
   right: {
      type: string;
      dock: string;
      header: boolean;
      collapsible: boolean;
      ui: string;
      cls: string;
      scrollable: { overflowY: boolean };
      collapsed: boolean;
      defaults: {
         labelPosition: string;
         width: string;
      };
      items: ConfigurePanelItem[];
   };
};

export type BuildCommonConfigurePanelItemsOptions = {
   t: Translate;
   values: GanttConfigPanelValues;
   includeTimeOffToggle: boolean;
   onProjectInformationChange: ToggleHandler;
   onAllocationInformationChange: ToggleHandler;
   onTimeOffInformationChange: ToggleHandler;
   onHideWeekendsChange: ToggleHandler;
   onRowHeightInput: NumberHandler;
   onRowHeightChange: NumberHandler;
   onTaskBorderRadiusInput: NumberHandler;
   onTaskBorderRadiusChange: NumberHandler;
   onAssignmentBarColorsChange: StringHandler;
   extraItems?: Array<ConfigurePanelItem | false | null | undefined>;
};

const isConfigurePanelItem = (
   item: ConfigurePanelItem | false | null | undefined,
): item is ConfigurePanelItem => Boolean(item);

export const getAssignmentBarColorOptions = (t: Translate) => [
   {
      value: AssignmentBarColors.PROJECT,
      text: t(
         "views.company.workforce_planning.gantt.configure_panel.assignment_bar_color_project",
      ),
   },
   {
      value: AssignmentBarColors.ALLOCATION_PERCENTAGE,
      text: t(
         "views.company.workforce_planning.gantt.configure_panel.assignment_bar_color_allocation_percentage",
      ),
   },
   {
      value: AssignmentBarColors.JOB_TITLES,
      text: t(
         "views.company.workforce_planning.gantt.configure_panel.assignment_bar_color_job_titles",
      ),
   },
   {
      value: AssignmentBarColors.OVERTIME,
      text: t(
         "views.company.workforce_planning.gantt.configure_panel.assignment_bar_color_overtime",
      ),
   },
   {
      value: AssignmentBarColors.STATUS,
      text: t("views.company.workforce_planning.gantt.configure_panel.assignment_bar_color_status"),
   },
];

export const buildCommonConfigurePanelItems = ({
   t,
   values,
   includeTimeOffToggle,
   onProjectInformationChange,
   onAllocationInformationChange,
   onTimeOffInformationChange,
   onHideWeekendsChange,
   onRowHeightInput,
   onRowHeightChange,
   onTaskBorderRadiusInput,
   onTaskBorderRadiusChange,
   onAssignmentBarColorsChange,
   extraItems = [],
}: BuildCommonConfigurePanelItemsOptions): ConfigurePanelItem[] => {
   const items: ConfigurePanelItem[] = [
      {
         tag: "span",
         html: t("views.company.workforce_planning.gantt.configure_panel.title"),
      },
      {
         type: "slidetoggle",
         label: t("views.company.workforce_planning.gantt.configure_panel.project_information"),
         labelPosition: "after",
         value: values.showProjectInformation,
         onChange: ({ value }: { value: boolean }) => onProjectInformationChange(value),
         id: "project-information",
      },
      {
         type: "slidetoggle",
         label: t("views.company.workforce_planning.gantt.configure_panel.allocation_information"),
         labelPosition: "after",
         value: values.showAllocationInformation,
         onChange: ({ value }: { value: boolean }) => onAllocationInformationChange(value),
         id: "allocation-information",
      },
   ];

   if (includeTimeOffToggle) {
      items.push({
         type: "slidetoggle",
         label: t("views.company.workforce_planning.gantt.configure_panel.time_off_information"),
         labelPosition: "after",
         value: values.showTimeoff,
         onChange: ({ value }: { value: boolean }) => onTimeOffInformationChange(value),
         id: "time-off",
      });
   }

   items.push(
      {
         type: "slidetoggle",
         label: t("views.company.workforce_planning.gantt.configure_panel.hide_weekends"),
         labelPosition: "after",
         value: values.hideWeekends,
         onChange: ({ value }: { value: boolean }) => onHideWeekendsChange(value),
         id: "hide-weekends",
      },
      {
         ref: "rowHeightSlider",
         type: "slider",
         label: t("views.company.workforce_planning.gantt.configure_panel.row_height"),
         labelPosition: "above",
         min: 24,
         max: 45,
         showValue: false,
         showTooltip: true,
         value: values.rowHeight,
         onInput: ({ value }: { value: number }) => onRowHeightInput(value),
         onChange: ({ value }: { value: number }) => onRowHeightChange(value),
         id: "row-height",
      },
      {
         ref: "borderRadiusSlider",
         type: "slider",
         label: t("views.company.workforce_planning.gantt.configure_panel.task_border_radius"),
         labelPosition: "above",
         min: 0,
         value: values.taskBorderRadius,
         max: 20,
         showValue: false,
         showTooltip: true,
         onInput: ({ value }: { value: number }) => onTaskBorderRadiusInput(value),
         onChange: ({ value }: { value: number }) => onTaskBorderRadiusChange(value),
         id: "border-radius",
      },
      {
         type: "combo",
         label: t("views.company.workforce_planning.gantt.configure_panel.assignment_bar_colors"),
         value: values.assignmentBarColors || AssignmentBarColors.PROJECT,
         items: getAssignmentBarColorOptions(t),
         onChange: ({ value }: { value: string }) => onAssignmentBarColorsChange(value),
         id: "assignment-bar-colors",
         style: "margin-top: 2em",
      },
      ...extraItems.filter(isConfigurePanelItem),
   );

   return items;
};

export const buildConfigurePanelStrip = (items: ConfigurePanelItem[]): ConfigurePanelStrip => ({
   right: {
      type: "panel",
      dock: "right",
      header: false,
      collapsible: true,
      ui: "procore",
      cls: "b-sidebar",
      scrollable: { overflowY: true },
      collapsed: true,
      defaults: {
         labelPosition: "above",
         width: "15em",
      },
      items,
   },
});
